import { useFeatureFlag } from '@liveauctioneers/hammer-ui-core/featureFlags/hooks';
import { CheckoutProvider } from '@liveauctioneers/hammer-ui-payments/checkout';
import {
    CheckoutResponse,
    Deployment,
    emptyMarketplaceProps,
    MarketplaceCode,
    PaymentProvider,
} from '@liveauctioneers/hammer-ui-payments/types';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getAuthToken, getBidderId } from '@/redux/modules/account/user/user.selectors';
import { getClientIpAddress } from '@/redux/modules/browser';
import { getCatalogDetailShippingDetails } from '@/redux/modules/catalog/detail/catalogDetail.selectors';
import { fetchInvoiceIfNeeded } from '@/redux/modules/checkout/checkout.actions';
import { getInvoiceById, getIsInvoiceLoaded } from '@/redux/modules/checkout/checkout.selectors';
import { getCheckoutShippingQuote } from '@/redux/modules/checkoutShippingQuote/checkoutShippingQuote.selectors';
import { getDeployment } from '@/redux/modules/config';
import { getSignifydSessionId } from '@/redux/modules/signifyd';
import { FeatureFlagKeys } from '@/types/FeatureFlags';
import { usePurchaseProtectionMessagingExperiment } from '@/utils/amplitude/experiments/hooks/usePurchaseProtectionMessagingExperiment';
import { getCheckoutUrl, getIdFromPath } from '@/utils/urls';

import CheckoutPageATGBody from './CheckoutPageATGBody';

const CheckoutPageATG = () => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const navigate = useNavigate();

    const signifydSessionId = useAppSelector(getSignifydSessionId);
    const deployment = useAppSelector(getDeployment);

    const token = useAppSelector(getAuthToken);
    const bidderId = useAppSelector((state) => getBidderId(state));

    const showPurchaseProtectionMessaging = usePurchaseProtectionMessagingExperiment();
    const inNewMobileCheckoutBeta = useFeatureFlag(FeatureFlagKeys.NewMobileCheckout);

    const [marketplaceProps, setMarketplaceProps] = useState({
        ...emptyMarketplaceProps,
    });
    const catalogId = getIdFromPath(location.pathname);
    const shippingQuote = useAppSelector(getCheckoutShippingQuote);
    const laShipping = shippingQuote.shippingAvailable;
    const { invoice } = useAppSelector((state) => getInvoiceById(state, catalogId, laShipping));
    const browserIp = useAppSelector(getClientIpAddress);

    const externalId = invoice.id;
    const invoiceLoaded = useAppSelector((state) => getIsInvoiceLoaded(state, catalogId));

    useEffect(() => {
        dispatch(fetchInvoiceIfNeeded({ catalogId, force: true }));
    }, [catalogId, dispatch]);

    useEffect(() => {
        if (invoiceLoaded && !invoice?.atgpay) {
            navigate(getCheckoutUrl(catalogId));
        }
    }, [catalogId, invoiceLoaded, invoice.atgpay, navigate]);

    const handlePaymentSuccess = (payload: CheckoutResponse) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { payment, salesOrder, transactions } = payload;
    };

    const risk = { browserIp, sessionId: signifydSessionId };

    const shippingDetails = useAppSelector((state) => getCatalogDetailShippingDetails(state, catalogId));
    const getCatalogShippingPolicy = useCallback(() => shippingDetails, [shippingDetails]);

    return (
        <div className="mx-auto my-0 max-w-[1240px] p-sm">
            <CheckoutProvider
                buyerId={externalId !== 0 ? bidderId.toString() : ''}
                deployment={deployment as Deployment}
                externalId={externalId !== 0 ? externalId.toString() : ''}
                getCatalogShippingPolicy={getCatalogShippingPolicy}
                inNewMobileCheckoutBeta={inNewMobileCheckoutBeta}
                isPurchaseProtectionEnabled={showPurchaseProtectionMessaging}
                marketplaceCode={MarketplaceCode.LiveAuctioneers}
                marketplaceProps={marketplaceProps}
                onPaymentSuccess={handlePaymentSuccess}
                paymentProvider={PaymentProvider.Payrix}
                risk={risk}
                token={token}
            >
                <CheckoutPageATGBody setMarketplaceProps={setMarketplaceProps} />
            </CheckoutProvider>
        </div>
    );
};

export default CheckoutPageATG;
